import { z } from 'zod';

const VOLUME_UNITS = ['mL', 'L', 'µL', 'nL'] as const;
const WEIGHT_UNITS = ['g', 'kg', 'mg', 'µg'] as const;
const COUNT_UNITS = ['count'] as const;

export const UNITS_BY_TYPE = {
  Volume: VOLUME_UNITS,
  Weight: WEIGHT_UNITS,
  Count: COUNT_UNITS,
} as const;

export function getUnitsForType(type: string): readonly string[] {
  return UNITS_BY_TYPE[type as keyof typeof UNITS_BY_TYPE] ?? VOLUME_UNITS;
}

export function getDefaultUnit(type: string): string {
  const units = getUnitsForType(type);
  return units[0];
}

const VOLUME_TO_UL: Record<string, number> = {
  L: 1_000_000,
  mL: 1_000,
  'µL': 1,
  nL: 0.001,
};

function toMicroliters(amount: number, unit: string): number | null {
  const factor = VOLUME_TO_UL[unit];
  if (factor === undefined) {
    return null;
  }
  return amount * factor;
}

export const addMaterialSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .max(100, 'Name too long'),

  quantityType: z.enum(['Volume', 'Weight', 'Count']),

  amount: z.coerce.number().min(0, 'Amount must be 0 or more'),

  units: z.string().min(1, 'Unit is required'),

  isEmpty: z.boolean(),

  containerType: z.enum([
    'Tube', 'PCR Tube', 'Microcentrifuge Tube', 'Falcon Tube',
    'Well Plate', 'Flask', 'Beaker', 'Vial', 'Bottle', 'Other',
  ]),

  containerName: z.string(),

  sameAsMaterial: z.boolean(),

  containerVolumeAmount: z.coerce.number().min(0, 'Container volume amount must be 0 or more'),

  containerVolumeUnits: z.string().min(1, 'Container volume unit is required'),

  vendor: z.string(),

  plateName: z.string(),

  rows: z.coerce.number().int(),

  columns: z.coerce.number().int(),

  wellVolumeAmount: z.coerce.number(),

  wellVolumeUnits: z.string(),

  wellAddresses: z.array(z.object({
    name: z.string(),
    row: z.string(),
    column: z.string(),
  })),
}).superRefine((data, ctx) => {
  // When not empty, unit must match quantity type
  if (!data.isEmpty) {
    const validUnits = getUnitsForType(data.quantityType);
    if (!validUnits.includes(data.units as never)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Unit must match quantity type',
        path: ['units'],
      });
    }
  }

  const isPlate = data.containerType === 'Well Plate';

  // Container name required when not plate and not sameAsMaterial
  if (!isPlate && !data.sameAsMaterial && data.containerName.trim().length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Container name is required',
      path: ['containerName'],
    });
  }

  // Plate-specific validation
  if (isPlate) {
    if (data.plateName.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Plate name is required',
        path: ['plateName'],
      });
    }
    if (data.rows < 1 || data.rows > 50) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Rows must be 1–50',
        path: ['rows'],
      });
    }
    if (data.columns < 1 || data.columns > 50) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Columns must be 1–50',
        path: ['columns'],
      });
    }
    if (data.wellVolumeAmount !== 0 && data.wellVolumeAmount < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Well volume must be positive',
        path: ['wellVolumeAmount'],
      });
    }
  }

  if (!data.isEmpty && data.quantityType === 'Volume') {
    const materialVolumeUl = toMicroliters(data.amount, data.units);
    if (materialVolumeUl !== null) {
      let containerCapacityUl: number | null = null;

      if (isPlate) {
        const wellVolumeUl = toMicroliters(data.wellVolumeAmount, data.wellVolumeUnits);
        if (wellVolumeUl !== null && wellVolumeUl > 0 && data.rows > 0 && data.columns > 0) {
          containerCapacityUl = wellVolumeUl * data.rows * data.columns;
        }
      } else {
        const containerVolumeUl = toMicroliters(data.containerVolumeAmount, data.containerVolumeUnits);
        if (containerVolumeUl !== null && containerVolumeUl > 0) {
          containerCapacityUl = containerVolumeUl;
        }
      }

      if (containerCapacityUl !== null && materialVolumeUl > containerCapacityUl) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Material quantity cannot be greater than container quantity',
          path: ['amount'],
        });
      }
    }
  }
});

export type AddMaterialFormSchema = z.infer<typeof addMaterialSchema>;

export const DEFAULT_FORM_VALUES: AddMaterialFormSchema = {
  name: '',
  quantityType: 'Volume',
  amount: 0,
  units: 'mL',
  isEmpty: false,
  containerType: 'Tube',
  containerName: '',
  sameAsMaterial: true,
  containerVolumeAmount: 0,
  containerVolumeUnits: 'mL',
  vendor: '',
  plateName: '',
  rows: 0,
  columns: 0,
  wellVolumeAmount: 0,
  wellVolumeUnits: 'µL',
  wellAddresses: [],
};
